import { Box, Button, Chip, TextField } from '@mui/material';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';

import { SearchCuration, SearchCurationRule, SearchExplanation } from '../models/search-quality';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

/**
 * Ansicht 3 — Warum dieses Ergebnis. The first ten results of a search and, next to
 * each, the index field that made it match. The search engine has this answer for
 * every hit; this is the one place it is asked for.
 */
export const SearchExplainView = () => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const [term, setTerm] = useState(searchParams.get('term') ?? '');
    const [explanation, setExplanation] = useState<SearchExplanation | null>(null);
    const [curation, setCuration] = useState<SearchCuration | null>(null);
    const [requesting, setRequesting] = useState(false);
    const [failed, setFailed] = useState(false);

    const explain = useCallback(
        (value: string) => {
            const trimmed = value.trim();
            if ('' === trimmed) {
                return;
            }
            setRequesting(true);
            setFailed(false);

            void dispatch(async (_dispatch, _getState, { api }) => {
                const [result, rules] = await Promise.all([
                    api.searchQuality.explain(trimmed),
                    api.searchQuality.getCuration(trimmed),
                ]);
                setExplanation(result.success ? result.result : null);
                setCuration(rules.success ? rules.result : null);
                setFailed(!result.success);
                setRequesting(false);
            });
        },
        [dispatch]
    );

    // A rule takes effect on the next search, so the list is asked again right
    // after — what the person sees is what a member would see now.
    const curate = (productId: number, rule: SearchCurationRule) => {
        if (!explanation) {
            return;
        }
        const current = explanation.term;
        void dispatch(async (_dispatch, _getState, { api }) => {
            await api.searchQuality.saveCuration(current, productId, rule);
            explain(current);
        });
    };

    const uncurate = (productId: number) => {
        if (!explanation) {
            return;
        }
        const current = explanation.term;
        void dispatch(async (_dispatch, _getState, { api }) => {
            await api.searchQuality.removeCuration(current, productId);
            explain(current);
        });
    };

    const ruleFor = (productId: number) => curation?.rules.find(rule => rule.productId === productId);

    useEffect(() => {
        const fromUrl = searchParams.get('term');
        if (fromUrl) {
            explain(fromUrl);
        }
    }, [searchParams, explain]);

    const submit = (event: FormEvent) => {
        event.preventDefault();
        const next = new URLSearchParams(searchParams);
        next.set('term', term.trim());
        setSearchParams(next);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <TextField
                    size="small"
                    value={term}
                    onChange={event => setTerm(event.target.value)}
                    placeholder={intl.formatMessage({ id: 'app.searchConsole.explain.placeholder' })}
                    sx={{ width: 360 }}
                />
                <Button type="submit" variant="contained" disabled={requesting || '' === term.trim()}>
                    <FormattedMessage id="app.searchConsole.explain.run" />
                </Button>
            </Box>

            {failed && (
                <Box sx={{ fontSize: '12px', color: tokens.color.crit }}>
                    <FormattedMessage id="app.searchConsole.explain.failed" />
                </Box>
            )}

            {!failed && !requesting && explanation === null && (
                <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                    <FormattedMessage id="app.searchConsole.explain.intro" />
                </Box>
            )}

            {explanation && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                        <FormattedMessage
                            id="app.searchConsole.explain.summary"
                            values={{ term: explanation.term, total: explanation.total.toLocaleString('de-AT') }}
                        />
                    </Box>
                    {curation && curation.rules.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                padding: '10px 12px',
                                border: `1px solid ${tokens.color.line}`,
                                borderRadius: '8px',
                                backgroundColor: tokens.color.surface,
                            }}
                        >
                            <Box sx={{ fontSize: '12px', fontWeight: 640, color: tokens.color.ink }}>
                                <FormattedMessage
                                    id="app.searchConsole.curation.rulesTitle"
                                    values={{ term: curation.term }}
                                />
                            </Box>
                            {curation.rules.map(rule => (
                                <Box
                                    key={rule.productId}
                                    sx={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '12px' }}
                                >
                                    <Chip
                                        size="small"
                                        color={rule.active ? ('pin' === rule.rule ? 'primary' : 'default') : 'default'}
                                        variant={rule.active ? 'filled' : 'outlined'}
                                        label={
                                            'pin' === rule.rule ? (
                                                <FormattedMessage
                                                    id="app.searchConsole.curation.pinned"
                                                    values={{ position: rule.position }}
                                                />
                                            ) : (
                                                <FormattedMessage id="app.searchConsole.curation.hidden" />
                                            )
                                        }
                                    />
                                    <Box sx={{ color: tokens.color.ink }}>{rule.name}</Box>
                                    <Box
                                        sx={{
                                            fontFamily: tokens.font.mono,
                                            fontSize: '11px',
                                            color: tokens.color.accent,
                                        }}
                                    >
                                        {rule.number}
                                    </Box>
                                    {rule.reason && <Box sx={{ color: tokens.color.ink3 }}>{rule.reason}</Box>}
                                    {rule.validUntil && (
                                        <Box sx={{ color: rule.active ? tokens.color.ink3 : tokens.color.warnLine }}>
                                            {rule.active ? (
                                                <FormattedMessage
                                                    id="app.searchConsole.curation.validUntil"
                                                    values={{
                                                        date: new Date(rule.validUntil).toLocaleDateString('de-AT'),
                                                    }}
                                                />
                                            ) : (
                                                <FormattedMessage id="app.searchConsole.curation.expired" />
                                            )}
                                        </Box>
                                    )}
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => uncurate(rule.productId)}
                                        sx={{ fontSize: '11px', marginLeft: 'auto' }}
                                    >
                                        <FormattedMessage id="app.searchConsole.curation.remove" />
                                    </Button>
                                </Box>
                            ))}
                            <Box sx={{ fontSize: '11px', color: tokens.color.ink3 }}>
                                <FormattedMessage id="app.searchConsole.curation.hint" />
                            </Box>
                        </Box>
                    )}
                    {explanation.hits.length === 0 && (
                        <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                            <FormattedMessage id="app.searchConsole.explain.empty" />
                        </Box>
                    )}
                    {explanation.hits.map((hit, index) => (
                        <Box
                            key={hit.productId}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '32px 1fr auto auto',
                                gap: '12px',
                                alignItems: 'center',
                                padding: '9px 12px',
                                backgroundColor: tokens.color.surface,
                                border: `1px solid ${tokens.color.line}`,
                                borderRadius: '8px',
                            }}
                        >
                            <Box
                                sx={{
                                    fontFamily: tokens.font.mono,
                                    fontSize: '12px',
                                    color: tokens.color.ink3,
                                    textAlign: 'right',
                                }}
                            >
                                {index + 1}
                            </Box>
                            <Box>
                                <Box sx={{ fontSize: '13px', color: tokens.color.ink }}>{hit.name}</Box>
                                <Box
                                    sx={{ fontFamily: tokens.font.mono, fontSize: '11px', color: tokens.color.accent }}
                                >
                                    {hit.number}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {ruleFor(hit.productId)?.rule === 'pin' && (
                                    <Chip
                                        size="small"
                                        color="primary"
                                        label={
                                            <FormattedMessage
                                                id="app.searchConsole.curation.pinned"
                                                values={{ position: ruleFor(hit.productId)?.position }}
                                            />
                                        }
                                    />
                                )}
                                {hit.matchedFields.map(field => (
                                    <Chip
                                        key={field}
                                        size="small"
                                        label={
                                            <FormattedMessage
                                                id={`app.searchConsole.field.${field}`}
                                                defaultMessage={field}
                                            />
                                        }
                                    />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', gap: '2px' }}>
                                {ruleFor(hit.productId)?.rule === 'pin' ? (
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => uncurate(hit.productId)}
                                        sx={{ fontSize: '11px' }}
                                    >
                                        <FormattedMessage id="app.searchConsole.curation.unpin" />
                                    </Button>
                                ) : (
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => curate(hit.productId, 'pin')}
                                        sx={{ fontSize: '11px' }}
                                    >
                                        <FormattedMessage id="app.searchConsole.curation.pin" />
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    variant="text"
                                    color="inherit"
                                    onClick={() => curate(hit.productId, 'hide')}
                                    sx={{ fontSize: '11px', color: tokens.color.ink3 }}
                                >
                                    <FormattedMessage id="app.searchConsole.curation.hide" />
                                </Button>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
